import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';

const PrivacyPolicy = () => {
  const dataProtection = [
    {
      icon: "Shield",
      title: "Seguridad",
      description: "Todos los datos personales se almacenan de manera segura en servidores protegidos con medidas de seguridad técnicas, físicas y administrativas."
    },
    {
      icon: "Lock",
      title: "Confidencialidad",
      description: "La información personal solo será accesible para el personal autorizado, y no será compartida ni vendida a terceros sin tu consentimiento."
    },
    {
      icon: "Key",
      title: "Cifrado",
      description: "Utilizamos cifrado SSL (Secure Sockets Layer) para garantizar que los datos enviados a través de nuestra plataforma estén protegidos contra accesos no autorizados."
    }
  ];

  const informationUsage = [
    {
      icon: "Database",
      title: "Recopilación de datos",
      description: "Recopilamos datos como tu nombre, correo electrónico, información de pago y preferencias para ofrecerte una experiencia personalizada y procesar las transacciones de descuentos."
    },
    {
      icon: "TrendingUp",
      title: "Mejora de servicios",
      description: "Utilizamos los datos recopilados para mejorar la plataforma, personalizar las promociones y descuentos que te ofrecemos, y para enviarte notificaciones relacionadas con tu cuenta."
    },
    {
      icon: "Mail",
      title: "Marketing",
      description: "Con tu consentimiento, podemos enviarte comunicaciones de marketing y ofertas promocionales sobre nuevos descuentos, actualizaciones de la plataforma y otros productos o servicios relacionados con BIZZNET."
    },
    {
      icon: "Users",
      title: "Compartir con terceros",
      description: "No compartimos ni vendemos tu información personal a terceros, salvo cuando sea necesario para cumplir con las leyes o para procesar transacciones relacionadas con los servicios que ofrecemos."
    }
  ];

  const userRights = [
    {
      icon: "Eye",
      title: "Acceso a tus datos",
      description: "Tienes derecho a acceder a la información que tenemos sobre ti en cualquier momento, a través de tu cuenta en la plataforma."
    },
    {
      icon: "Edit",
      title: "Corrección de datos",
      description: "Si encuentras que la información que tenemos sobre ti es incorrecta o incompleta, puedes corregirla directamente a través de la plataforma o contactarnos para hacerlo."
    },
    {
      icon: "Trash2",
      title: "Eliminación de datos",
      description: "Si deseas eliminar tu cuenta o retirar tu consentimiento para el uso de tus datos, puedes hacerlo contactándonos directamente. Ten en cuenta que, si eliminas tu cuenta, perderás el acceso a los descuentos y otros servicios de la plataforma."
    },
    {
      icon: "Share2",
      title: "Derecho a la portabilidad",
      description: "Tienes derecho a recibir tus datos en un formato estructurado y de uso común, si deseas transferirlos a otro proveedor de servicios."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Políticas de Privacidad - BIZZNET</title>
        <meta name="description" content="Políticas de privacidad de BIZZNET. Conoce cómo protegemos y manejamos tu información personal con transparencia y seguridad." />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 py-12 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <Link to="/" className="text-white/90 hover:text-white transition-colors drop-shadow-md">
                <Icon name="ArrowLeft" size={24} />
              </Link>
              <h1 className="text-4xl md:text-5xl font-bold text-white drop-shadow-lg">
                Políticas de Privacidad
              </h1>
            </div>
            <p className="text-xl md:text-2xl font-medium max-w-3xl text-white/95 drop-shadow-md">
              Tu privacidad es nuestra prioridad. Aquí te explicamos cómo manejamos tu información
            </p>
          </div>
        </section>

        {/* Introduction Section */}
        <section className="py-12 px-6 bg-card">
          <div className="max-w-7xl mx-auto">
            <div className="bg-background border border-border rounded-xl p-6">
              <div className="flex items-start gap-4">
                <div className="bg-primary/10 p-3 rounded-xl flex-shrink-0">
                  <Icon name="ShieldCheck" size={28} className="text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-foreground mb-3">Protección de Datos</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Te garantizamos que tu información personal será manejada con el máximo nivel de seguridad y transparencia. Utilizamos tecnologías avanzadas de cifrado para proteger tu información durante el proceso de transmisión y almacenamiento.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Data Protection Section */}
        <section className="py-12 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-3 gap-6">
              {dataProtection?.map((item, index) => (
                <div key={index} className="bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-shadow">
                  <div className="bg-primary/10 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                    <Icon name={item?.icon} size={28} className="text-primary" />
                  </div>
                  <h4 className="text-lg font-bold text-foreground mb-3">{item?.title}</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item?.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Information Usage Section */}
        <section className="py-12 px-6 bg-card">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <Icon name="Info" size={32} className="text-primary" />
              <h2 className="text-3xl md:text-4xl font-bold text-foreground">Uso de la Información</h2>
            </div>
            <p className="text-muted-foreground mb-8 text-lg">
              Te explicamos cómo utilizamos los datos que recopilamos y para qué fines. Toda la información recopilada será utilizada para mejorar la experiencia del usuario en nuestra plataforma y ofrecerte los mejores servicios posibles.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {informationUsage?.map((item, index) => (
                <div key={index} className="bg-background border border-border rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-primary/10 p-3 rounded-xl flex-shrink-0">
                      <Icon name={item?.icon} size={28} className="text-primary" />
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-foreground mb-2">{item?.title}</h4>
                      <p className="text-sm text-muted-foreground leading-relaxed">{item?.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* User Rights Section */}
        <section className="py-12 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <Icon name="UserCheck" size={32} className="text-primary" />
              <h2 className="text-3xl md:text-4xl font-bold text-foreground">Derechos del Usuario</h2>
            </div>
            <p className="text-muted-foreground mb-8 text-lg">
              Como usuario, tienes el derecho de acceder, corregir o eliminar tus datos personales en cualquier momento. Aquí te explicamos cómo puedes ejercer tus derechos:
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {userRights?.map((right, index) => (
                <div key={index} className="bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="bg-primary/10 p-3 rounded-xl flex-shrink-0">
                      <Icon name={right?.icon} size={28} className="text-primary" />
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-foreground mb-2">{right?.title}</h4>
                      <p className="text-sm text-muted-foreground leading-relaxed">{right?.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Policy Changes and Acceptance */}
        <section className="py-12 px-6 bg-card">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Policy Changes */}
              <div className="bg-background border border-border rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-primary/10 p-3 rounded-xl">
                    <Icon name="RefreshCw" size={32} className="text-primary" />
                  </div>
                  <h3 className="text-2xl font-bold text-foreground">Cambios en la Política de Privacidad</h3>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  BIZZNET se reserva el derecho de modificar esta Política de Privacidad en cualquier momento. Cualquier cambio será publicado en este sitio web inmediatamente después de su vigor. Te recomendamos revisar esta página periódicamente para estar al tanto de cualquier actualización.
                </p>
              </div>

              {/* Acceptance */}
              <div className="bg-background border border-border rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-primary/10 p-3 rounded-xl">
                    <Icon name="CheckCircle" size={32} className="text-primary" />
                  </div>
                  <h3 className="text-2xl font-bold text-foreground">Aceptación de la Política de Privacidad</h3>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  Al utilizar nuestros servicios, aceptas la recopilación y el uso de tu información personal de acuerdo con los términos establecidos en esta Política de Privacidad. Si no estás de acuerdo con alguna de las condiciones, te recomendamos que no utilices nuestra plataforma.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-6 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700">
          <div className="max-w-4xl mx-auto text-center">
            <Icon name="Lock" size={48} className="mx-auto mb-6 text-white drop-shadow-lg" />
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white drop-shadow-lg">
              ¿Tienes Dudas sobre tu Privacidad?
            </h2>
            <p className="text-lg mb-8 text-white/95 drop-shadow-md">
              Si tienes alguna pregunta sobre nuestras políticas de privacidad, no dudes en contactarnos
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/faq"
                className="bg-white text-gray-900 px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-colors inline-flex items-center justify-center gap-2 shadow-lg"
              >
                <Icon name="HelpCircle" size={20} />
                Preguntas Frecuentes
              </Link>
              <Link
                to="/"
                className="bg-white/10 text-white border-2 border-white/30 px-8 py-4 rounded-xl font-semibold hover:bg-white/20 transition-colors inline-flex items-center justify-center gap-2 backdrop-blur-sm"
              >
                <Icon name="Home" size={20} />
                Volver al Inicio
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default PrivacyPolicy;